import { type ApiClient, getResponseData } from "@/modules/api/apiClient.ts";
import { getResourceIconClass } from "@/modules/api/resourceTypes.tsx";
import { raiseRequestToast, type Toast } from "@/modules/notifications/toast.tsx";
import type { ItemWithPrice } from "@/modules/pricing/resourcePricing.ts";
import { getQueryKey as getBucketQueryKey } from "@/modules/storage/bucket/bucketApi.ts";
import { storageAccountLink } from "@/modules/storage/links.ts";
import { jsonEncodedBody } from "@/utils/fetchClient.ts";
import { optimisticPromise } from "@/utils/query/optimisticPromise.ts";
import { produceQueryData } from "@/utils/query/produceQueryData.ts";
import { atomFromStandardQueryOptions } from "@/utils/query/queryAtom.ts";
import { mutationOptions } from "@/utils/query/runMutation.ts";
import type { QueryClient } from "@tanstack/query-core";
import { type QueryKey, queryOptions } from "@tanstack/react-query";
import type {
    StorageAccount,
    StorageAccountCreateBody,
    StorageAccountKey,
    StorageAccountKeyCreateBody,
    StorageAccountKeyUpdateBody,
    StorageAccountUpdateBody,
} from "@warrenio/api-spec/spec.oats.gen";
import { mapFromEntries } from "@warrenio/utils/collections/maps";

//#region Types/Contracts
export interface Params {
    location: string;
}

export interface StorageAccountWithType extends StorageAccount {
    $type: "storage_account";
}

export interface StorageAccountWithPrice extends StorageAccountWithType, ItemWithPrice {
    size_bytes: number;
    number_of_buckets: number;
}

type Response = Map<StorageAccount["uuid"], StorageAccountWithType>;
const emptyResponse: Response = new Map();

const toastOptions: Partial<Toast> = { icon: getResourceIconClass("storage_account") };

//#endregion

//#region helper functions

function getQueryKey(params?: Params): QueryKey {
    return params == null ? ["storage/accounts/list"] : ["storage/accounts/list", params.location];
}

function accountFromData(data: StorageAccount): StorageAccountWithType {
    return { ...data, $type: "storage_account" };
}

//#endregion helper functions

//#region Queries

/** @returns Map of {@link StorageAccount}s keyed by uuid */
export function storageAccountListQuery(apiClient: ApiClient, params?: Params) {
    return queryOptions({
        queryKey: getQueryKey(params),
        queryFn: async ({ signal }) => {
            const response = getResponseData(await apiClient.GET("/storage/accounts", { signal }));

            return mapFromEntries(response, (o) => [o.uuid, accountFromData(o)]);
        },
    });
}

export const storageAccountListQueryAtom = atomFromStandardQueryOptions(storageAccountListQuery);

const storageKeysQueryKey: QueryKey = ["object_storage_keys"];

function storageAccountKeysQueryKey(storageAccountUuid: string): QueryKey {
    return [...storageKeysQueryKey, storageAccountUuid];
}

export function getStorageAccountKeysQuery(apiClient: ApiClient, storageAccountUuid: string) {
    return queryOptions({
        queryKey: storageAccountKeysQueryKey(storageAccountUuid),
        queryFn: async ({ signal }): Promise<StorageAccountKey[]> =>
            getResponseData(
                await apiClient.GET("/storage/accounts/{uuid}/keys", {
                    signal,
                    params: {
                        path: {
                            uuid: storageAccountUuid,
                        },
                    },
                }),
            ),
    });
}
//#endregion Queries

//#region Mutations
export function createStorageAccountMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["storage/accounts/create"],
        async mutationFn({ body }: { body: StorageAccountCreateBody }) {
            const result = getResponseData(
                await apiClient.POST("/storage/accounts", {
                    ...jsonEncodedBody,
                    body,
                }),
            );
            return accountFromData(result);
        },
        onSuccess(res) {
            produceQueryData(queryClient, getQueryKey(), emptyResponse, (data) => {
                data.set(res.uuid, res);
            });
        },
        onSettled(res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Storage account created",
                error: "Failed to create storage account",
                successType: "success",
                viewLink: res ? storageAccountLink(res) : undefined,
            });

            optimisticPromise(queryClient.invalidateQueries({ queryKey: getQueryKey() }));
        },
    });
}

export function changeStorageAccountMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["storage/accounts/update"],
        async mutationFn({ uuid, body }: { uuid: string; body: StorageAccountUpdateBody }) {
            return getResponseData(
                await apiClient.PATCH("/storage/accounts/{uuid}", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { uuid } },
                }),
            );
        },
        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Storage account updated",
                error: "Failed to update storage account",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey() });
            optimisticPromise(queryClient.invalidateQueries({ queryKey: getBucketQueryKey() }));
        },
    });
}

export function deleteStorageAccountMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["storage/account/delete"],
        async mutationFn({ uuid }: { uuid: string }) {
            return getResponseData(
                await apiClient.DELETE("/storage/accounts/{uuid}", {
                    params: { path: { uuid } },
                }),
            );
        },

        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Storage account deleted",
                error: "Failed to delete storage account",
            });
            await queryClient.invalidateQueries({ queryKey: getQueryKey() });
        },
    });
}

export function generateObjectStorageKeyMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["storage/bucket/key/create"],
        async mutationFn({ body, uuid }: { body: StorageAccountKeyCreateBody; uuid: string }) {
            return getResponseData(
                await apiClient.POST("/storage/accounts/{uuid}/keys", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { uuid } },
                }),
            );
        },
        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Storage access key generated",
                error: "Failed to generate storage access key",
            });
            await queryClient.invalidateQueries({ queryKey: storageKeysQueryKey });
        },
    });
}

export function changeObjectStorageKeyMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["storage/bucket/key/create"],
        async mutationFn({
            body,
            uuid,
            access_key,
        }: {
            body: StorageAccountKeyUpdateBody;
            uuid: string;
            access_key: string;
        }) {
            return getResponseData(
                await apiClient.PATCH("/storage/accounts/{uuid}/keys/{access_key}", {
                    ...jsonEncodedBody,
                    body,
                    params: { path: { uuid, access_key } },
                }),
            );
        },
        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Storage access key updated",
                error: "Failed to update storage access key",
            });
            await queryClient.invalidateQueries({ queryKey: storageKeysQueryKey });
        },
    });
}

export function deleteObjectStorageKeyMutation(apiClient: ApiClient, queryClient: QueryClient) {
    return mutationOptions({
        mutationKey: ["storage/bucket/key/delete"],
        async mutationFn({ uuid, access_key }: { uuid: string; access_key: string }) {
            return getResponseData(
                await apiClient.DELETE("/storage/accounts/{uuid}/keys/{access_key}", {
                    params: { path: { uuid, access_key } },
                }),
            );
        },
        async onSettled(_res, err) {
            raiseRequestToast(err, {
                ...toastOptions,
                success: "Storage access key deleted",
                error: "Failed to delete storage access key",
            });
            await queryClient.invalidateQueries({ queryKey: storageKeysQueryKey });
        },
    });
}
//#endregion Mutations
